/**
 * Block 14 «Раскрой» — local-first персистенция проектов раскроя (PR #25 #168,
 * ADR-0090). IndexedDB-хранилище проектов на устройстве: «Сохранить проект»
 * работает офлайн/везде без бэкенда (как `WarehouseStore` для остатков, но
 * проекты крупнее — полный `ProjectData` с `lastResult`, нужен IDB не localStorage).
 *
 * **Отдельная БД `pomoshnik-cutting-v1`** (НЕ общая `pomoshnik-offline-v1`
 * калькулятора): у той «wipe+recreate всех stores» при version bump (B-145) —
 * связывать cutting с её версионированием и риском вайпа чужих черновиков нельзя.
 * Один object store `projects` (keyPath `id`).
 *
 * Серверный sync — аддитивный best-effort в сторе (`useCuttingStore`), не здесь:
 * этот модуль — durable источник истины устройства. Терпимость к недоступному IDB
 * (приватный режим/квота) — caller (`useCuttingStore`) ловит reject и не крэшит UI.
 * `updatedAt` проставляет caller (детерминизм тестов, без `Date.now()` внутри).
 *
 * Запись = `CuttingProjectState` (редактируемые поля проекта, см. projectSync) +
 * `updatedAt`/`syncStatus`. `data: ProjectData` несёт `lastResult` (контракт
 * ADR-0065 НЕ меняем).
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { CuttingProjectState } from '../state/projectSync';

/** Статус серверной синхронизации записи (best-effort sync, ADR-0090). */
export type CuttingSyncStatus = 'local' | 'synced';

/** Локальная запись проекта раскроя (IDB). */
export interface CuttingProjectRecord extends CuttingProjectState {
  /** ISO-время последнего сохранения (caller проставляет) — сортировка Истории. */
  updatedAt: string;
  /** 'synced' если серверный best-effort upsert подтвердил; иначе 'local'. */
  syncStatus: CuttingSyncStatus;
}

interface CuttingDBSchema extends DBSchema {
  projects: { key: string; value: CuttingProjectRecord };
}

export const CUTTING_DB_NAME = 'pomoshnik-cutting-v1';
const CUTTING_DB_VERSION = 1;
const STORE = 'projects';

let dbPromise: Promise<IDBPDatabase<CuttingDBSchema>> | null = null;

function openCuttingDb(): Promise<IDBPDatabase<CuttingDBSchema>> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IDB_UNAVAILABLE: IndexedDB not supported'));
  }
  if (!dbPromise) {
    dbPromise = openDB<CuttingDBSchema>(CUTTING_DB_NAME, CUTTING_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
      },
      terminated() {
        // Браузер убил connection (low memory) — сбросить кэш для пере-открытия.
        dbPromise = null;
      },
    });
  }
  return dbPromise;
}

/** Upsert записи проекта (idempotent по `id`). */
export async function saveProject(record: CuttingProjectRecord): Promise<void> {
  const db = await openCuttingDb();
  await db.put(STORE, record);
}

/** Прочитать проект по id (`undefined` если нет). */
export async function getProject(id: string): Promise<CuttingProjectRecord | undefined> {
  const db = await openCuttingDb();
  return db.get(STORE, id);
}

/** Все проекты, отсортированные по `updatedAt` DESC (свежие первыми) — для Истории. */
export async function listProjects(): Promise<CuttingProjectRecord[]> {
  const db = await openCuttingDb();
  const all = await db.getAll(STORE);
  return all.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

/** Удалить проект по id. */
export async function deleteProject(id: string): Promise<void> {
  const db = await openCuttingDb();
  await db.delete(STORE, id);
}

/** Очистить все проекты (тесты / logout). */
export async function clear(): Promise<void> {
  const db = await openCuttingDb();
  await db.clear(STORE);
}

/** Для тестов: сбросить кэш соединения (после смены IDBFactory). */
export function _resetCuttingDbCache(): void {
  dbPromise = null;
}

export const CuttingProjectStore = {
  saveProject,
  getProject,
  listProjects,
  deleteProject,
  clear,
};
